import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useState } from 'react';
import type { PdlAdjudicatorTraceRow, PdlTracePhase } from '../../lib/pdlAdjudicatorCopy';

type Section = {
  phase: PdlTracePhase;
  label: string;
  rows: PdlAdjudicatorTraceRow[];
};

function TraceRow({ row }: { row: PdlAdjudicatorTraceRow }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
        gap: { xs: 0.25, sm: 1.5 },
        alignItems: 'baseline',
        py: 0.75,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.35 }}>
          {row.label}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
          {row.detail}
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          textAlign: { xs: 'left', sm: 'right' },
          color: 'text.primary',
          wordBreak: 'break-word',
        }}
      >
        {row.value}
      </Typography>
    </Box>
  );
}

function TraceSection({ section }: { section: Section }) {
  const theme = useTheme();
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 800,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: 'text.secondary',
          fontSize: '0.65rem',
          display: 'block',
          mb: 0.5,
        }}
      >
        {section.label}
      </Typography>
      <Box
        sx={{
          borderRadius: 1.25,
          border: '1px solid',
          borderColor: 'divider',
          px: 1.1,
          bgcolor:
            theme.palette.mode === 'light'
              ? alpha(theme.palette.common.black, 0.015)
              : alpha(theme.palette.common.white, 0.03),
        }}
      >
        {section.rows.map((row, idx) => (
          <TraceRow key={`${section.phase}-${row.code}-${idx}`} row={row} />
        ))}
      </Box>
    </Box>
  );
}

export function PdlTechnicalTracePanel({ sections }: { sections: Section[] }) {
  const legacySections = sections.filter((s) => s.phase !== 'bank');
  const bankSection = sections.find((s) => s.phase === 'bank');
  const [bankOpen, setBankOpen] = useState(false);

  if (sections.length === 0) return null;

  return (
    <Stack spacing={1.25} data-testid="application-decision-engine-pdl-trace">
      {legacySections.map((section) => (
        <TraceSection key={section.phase} section={section} />
      ))}

      {bankSection && bankSection.rows.length > 0 ? (
        <Accordion
          expanded={bankOpen}
          onChange={(_, expanded) => setBankOpen(expanded)}
          disableGutters
          elevation={0}
          sx={{
            borderRadius: '10px !important',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'transparent',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreRoundedIcon />}
            sx={{ minHeight: 40, px: 1.1, '& .MuiAccordionSummary-content': { my: 0.5 } }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
              Bank payment room steps (audit only)
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, px: 1.1, pb: 1 }}>
            <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 0.5 }}>
              {bankSection.rows.map((row, idx) => (
                <TraceRow key={`bank-${row.code}-${idx}`} row={row} />
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      ) : null}
    </Stack>
  );
}
